import React, { useEffect, useState } from 'react';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { ElementsConsumer, Elements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { updatePaymentStatus } from '../../features/paymentMethod/paymentMethodSlice';
import { Row, Col } from 'react-bootstrap';
import './SmashStripe.scss';
import { useAppDispatch } from '../../hooks/redux/hooks';
import { useCardToken } from './useSmashStripe';

const SmashStripe = ({ stripe, elements}: { stripe: Stripe | null, elements: StripeElements | null}) => {
    const dispatch = useAppDispatch();
    const [cardToken, generateCardToken, resetToken, tokenError] = useCardToken(stripe);
    const [cardState, setCardState] = useState({ cardNumber: false, cardExpiry: false, cardCvc: false });
    const [cardReady, setCardReady] = useState(false);
    const [generatingStripeToken, setGeneratingStripeToken] = useState(false);
    
    useEffect(() => {
        if(tokenError) { 
            setGeneratingStripeToken(false);
            dispatch(updatePaymentStatus({status:'stripeTokenKO', stripeError: tokenError }));
        }
        if (!cardReady && cardState.cardNumber && cardState.cardExpiry && cardState.cardCvc) {
            setCardReady(true);
        }

        if (cardReady && !cardToken && !generatingStripeToken && elements && !tokenError) {
            setGeneratingStripeToken(true);
            const card = elements.getElement(CardNumberElement);
            generateCardToken(card);
            dispatch(updatePaymentStatus({status:'generatingStripeToken'}));            
        }
        if(cardReady && cardToken && generatingStripeToken) {
            setGeneratingStripeToken(false);
            dispatch(updatePaymentStatus({status: 'stripeTokenOK', token: cardToken}));
        }

    }, [cardReady, generatingStripeToken, cardToken, cardState, tokenError]);

    const handleCardUpdate = async (event: any) => {
        const { elementType, complete, error } = event;
        if (!stripe || !elements) {
            return;
        }
        if (error || (elementType && !complete)) {
            resetToken();
            setCardReady(false);
            setCardState(prevState => ({ ...prevState, [elementType]: false }));
        }

        if (elementType && complete) {
            setCardState(prevState => ({ ...prevState, [elementType]: true }));
        }
    }

    return (
        <>
            <Row className="mb-3">
                <Col>
                    <CardNumberElement onChange={handleCardUpdate} className="container-input-card-number" />
                </Col>
            </Row>
            <Row className="mb-3">
                <Col>
                    <CardExpiryElement onChange={handleCardUpdate} className="container-input-card-expirationdate" />
                </Col>
                <Col>
                    <CardCvcElement onChange={handleCardUpdate} className="container-input-card-cvc" />
                </Col>
            </Row>
        </>
    )
}

const InjectedStripe = () => (
    <ElementsConsumer>
        {({ stripe, elements }) => (<SmashStripe stripe={stripe} elements={elements} />)}
    </ElementsConsumer>
);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string);
const WrappedStripe = () => (
    <Elements stripe={stripePromise}>
        <InjectedStripe />
    </Elements>
)

export default WrappedStripe;
